import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import MobileMenu from './MobileMenu';
import { useWebSocket } from '../hooks/useWebSocket';
import { BACKEND_URL } from '../lib/backendUrl';
import '../styles/Landing.css';

const ggImage = 'https://res.cloudinary.com/de2rvuvhd/image/upload/v1775667695/jssnexus/jssnexus/landing/fxbjchjnbjpnihrlouee.png';
const aboutImage = 'https://res.cloudinary.com/de2rvuvhd/image/upload/v1775135744/jssnexus/h9t8euq0cxnt8idrnmyy.jpg';
const bgImage = 'https://res.cloudinary.com/de2rvuvhd/image/upload/v1775135737/jssnexus/wpmaqgtmv2t9mun4vyfu.jpg';

interface Event {
  id: string;
  name: string;
  description: string;
  type: 'event' | 'workshop' | 'exhibition' | 'seminar' | 'competition' | string;
  club_id?: string;
  club_name?: string;
  date: string;
  image?: string;
  fee: number;
  created_by?: string;
  created_at?: string;
}

// GPU-optimized animation variants to reduce redundant recreations
const slideInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const slideInLeftVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 }
};

const slideInRightVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 }
};

const sharedTransition = { duration: 0.6 };
const staggeredTransition = { duration: 0.6, delay: 0.2 };

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribe, isConnected } = useWebSocket();
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const API_URL = BACKEND_URL;

  // Fetch events once on mount
  const fetchEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const response = await axios.get(`${API_URL}/public/events`);
      const eventList = Array.isArray(response.data) ? response.data : response.data.data || [];

      // Sort events by date and get upcoming events (future dates only)
      const now = new Date();
      const upcomingEvents = eventList
        .filter((evt: any) => {
          const eventDate = evt.date ? new Date(evt.date) : null;
          return eventDate && eventDate > now;
        })
        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEventsError('Failed to load events');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [API_URL]);

  // Initial fetch + WebSocket subscription for new events
  useEffect(() => {
    fetchEvents();

    // Subscribe to new events via WebSocket (no polling!)
    const unsubscribe = subscribe('/topic/events', (newEvent: Event) => {
      setEvents((prev) => {
        // Check if event is in the future
        const eventDate = newEvent.date ? new Date(newEvent.date) : null;
        const now = new Date();
        if (eventDate && eventDate > now) {
          // Add new event and re-sort
          const updated = [...prev, newEvent].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );
          return updated;
        }
        return prev;
      });
    });

    return () => {
      unsubscribe?.();
    };
  }, [fetchEvents, subscribe]);
  
  const handleRefreshEvents = async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const response = await axios.get(`${API_URL}/public/events`, {
        params: { _t: Date.now() }
      });
      const eventList = Array.isArray(response.data) ? response.data : response.data.data || [];
      
      const now = new Date();
      const upcomingEvents = eventList
        .filter((evt: any) => {
          const eventDate = evt.date ? new Date(evt.date) : null;
          return eventDate && eventDate > now;
        })
        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Failed to refresh events:', error);
      setEventsError('Failed to load events');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Events carousel scroll
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = useCallback(() => {
    const el = eventsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScrollButtons();
  }, [events, eventsLoading, checkScrollButtons]);

  const scrollEvents = (direction: 'left' | 'right') => {
    const el = eventsScrollRef.current;
    if (!el) return;
    const scrollAmount = 340; // roughly one card width + gap
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(checkScrollButtons, 350);
  };

  // Memoize dashboard button label to prevent unnecessary re-renders
  const dashboardLabel = useMemo(() => 
    user && user.role ? `${user.role.toUpperCase()} DASHBOARD →` : `ENTER DASHBOARD →`,
    [user]
  );

  const handleEnterDashboard = () => {
    if (user?.role) {
      const normalizedRole = user.role.toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole];
      navigate(dashboardPath || '/public/');
    } else {
      navigate('/public/');
    }
  };

  // Header Navigation
  const Header = () => (
    <motion.header 
      className="landing-header"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="header-container">
        <div className="header-logo">
          <h1>NEXUS</h1>
        </div>

        {/* Mobile Menu Component */}
        <MobileMenu />

        {/* Desktop Navigation */}
        <nav className="header-nav">
          <a href="#hero" className="nav-link">Home</a>
          <a href="#about" className="nav-link">About</a>
          <a href="#events" className="nav-link">Events</a>
          <a href="#contact" className="nav-link">Contact</a>
        </nav>

        {/* Desktop Enter Dashboard Button */}
        <div className="header-auth-btn-desktop">
          <button
            className="header-signin-btn"
            onClick={handleEnterDashboard}
          >
            ENTER DASHBOARD →
          </button>
        </div>
      </div>
    </motion.header>
  );

  // Hero Section - OPTIMIZED: Reduced observers, GPU-accelerated transforms
  const HeroSection = () => (
    <section id="hero" className="hero-section">
      <motion.div 
        className="hero-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          {/* Image animates on viewport - single observer for section */}
          <motion.div
            className="hero-image-wrapper"
            initial="hidden"
            whileInView="visible"
            variants={slideInLeftVariants}
            transition={sharedTransition}
            viewport={{ once: true, margin: "-50px" }}
          >
            <img src={ggImage} alt="Events" className="hero-image" />
          </motion.div>

          {/* Explore Button */}
          <motion.div
            className="hero-cta-wrapper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.button
              className="hero-explore-btn"
              onClick={handleEnterDashboard}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ y: 0, scale: 0.98 }}
            >
              EXPLORE →
            </motion.button>
          </motion.div>
        </div>

        {/* GPU-optimized floating orbs with CSS animations (not JS) */}
        <div className="hero-bg-elements">
          <div className="floating-orb orb-1"></div>
          <div className="floating-orb orb-2"></div>
          <div className="floating-orb orb-3"></div>
        </div>
      </motion.div>
    </section>
  );

  // About Section - OPTIMIZED: Single viewport observer, GPU transforms
  const AboutSection = () => (
    <section id="about" className="about-section">
      <motion.div
        className="about-container"
        initial="hidden"
        whileInView="visible"
        variants={slideInVariants}
        transition={sharedTransition}
        viewport={{ once: true, margin: "-50px" }}
      >
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={sharedTransition}
          viewport={{ once: true, margin: "-50px" }}
        >
          WELCOME TO JSSATE OFFICIAL EVENTS PAGE
        </motion.h2>

        <div className="about-content">
          <motion.div
            className="about-image-wrapper"
            initial="hidden"
            whileInView="visible"
            variants={slideInLeftVariants}
            transition={sharedTransition}
            viewport={{ once: true, margin: "-50px" }}
          >
            <img src={aboutImage} alt="JSSATE Campus" className="about-image" />
            <div className="image-badge">JSSATE, Bengaluru</div>
          </motion.div>

          <motion.div
            className="about-text"
            initial="hidden"
            whileInView="visible"
            variants={slideInRightVariants}
            transition={staggeredTransition}
            viewport={{ once: true, margin: "-50px" }}
          >
            <p className="about-intro">
              <strong>JSS Academy of Technical Education (JSSATE)</strong> was established in 1997 at Bangalore and is under the umbrella of JSS Mahavidyapeetha, Mysore.
            </p>

            <p className="about-description">
              JSSATE is the result of the vision of our President, His Holiness Jagadguru Sri Shivarathri Deshikendra Mahaswamiji to proactively participate in establishing a world-class Institution for Technical Education.
            </p>

            <p className="about-description">
              The Campus is located on a sprawling <strong>21.17 acres</strong> of land surrounded by lush green plantation on the South-Western edge of Bangalore City. The institution is affiliated to <strong>Visvesvaraya Technological University (VTU), Belagavi, India.</strong>
            </p>

            <div className="about-highlights">
              {/* GPU-optimized: translateY only (no scale) */}
              <motion.div 
                className="highlight-item"
                whileHover={{ y: -3 }}
              >
                <span className="highlight-number">1997</span>
                <span className="highlight-label">Established</span>
              </motion.div>
              <motion.div 
                className="highlight-item"
                whileHover={{ y: -3 }}
              >
                <span className="highlight-number">21.17</span>
                <span className="highlight-label">Acres Campus</span>
              </motion.div>
              <motion.div 
                className="highlight-item"
                whileHover={{ y: -3 }}
              >
                <span className="highlight-number">VTU</span>
                <span className="highlight-label">Affiliated</span>
              </motion.div>
            </div>

            <motion.button
              className="about-cta-btn"
              whileHover={{ y: -3 }}
              whileTap={{ y: -1 }}
              onClick={() => window.open('https://www.jssateb.ac.in/', '_blank')}
            >
              Learn More →
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );

  // Events Preview Section - OPTIMIZED: Grouped animations, GPU transforms, replaced animated box-shadow
  const EventsSection = () => (
    <section id="events" className="events-section">
      <motion.div
        className="events-container"
        initial="hidden"
        whileInView="visible"
        variants={slideInVariants}
        transition={sharedTransition}
        viewport={{ once: true, margin: "-50px" }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={sharedTransition}
            viewport={{ once: true, margin: "-50px" }}
          >
            Upcoming Events
          </motion.h2>
          <motion.button
            style={{
              padding: '0.6rem 1.2rem',
              background: 'rgba(0, 255, 255, 0.2)',
              border: '1px solid rgba(0, 255, 255, 0.5)',
              borderRadius: '6px',
              color: '#00ffff',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              fontFamily: "'Rajdhani', monospace",
            }}
            onClick={handleRefreshEvents}
            whileHover={{ background: 'rgba(0, 255, 255, 0.3)' }}
            whileTap={{ scale: 0.95 }}
            disabled={eventsLoading}
          >
            Refresh
          </motion.button>
        </div>

        <div className="events-carousel-wrapper">
          {canScrollLeft && (
            <button className="events-scroll-btn events-scroll-left" onClick={() => scrollEvents('left')} aria-label="Scroll left">
              ‹
            </button>
          )}
          {canScrollRight && (
            <button className="events-scroll-btn events-scroll-right" onClick={() => scrollEvents('right')} aria-label="Scroll right">
              ›
            </button>
          )}
        <div className="events-grid" ref={eventsScrollRef} onScroll={checkScrollButtons}>
          {eventsLoading ? (
            <div className="loading-container">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="loading-spinner"
              >
              </motion.div>
              <p>Loading events...</p>
            </div>
          ) : eventsError ? (
            <div className="error-container">
              <p>{eventsError}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="no-events-container">
              <p>No upcoming events at the moment</p>
            </div>
          ) : (
            events.map((event, index) => {
              const formatDate = (dateString: string) => {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
              };

              const getEventTypeIcon = () => {
                switch (event.type?.toLowerCase()) {
                  case 'workshop':
                    return '';
                  case 'seminar':
                    return '';
                  case 'competition':
                    return '';
                  case 'exhibition':
                    return '';
                  case 'event':
                    return '';
                  default:
                    return '';
                }
              };

              return (
                <motion.div
                  key={event.id}
                  className="event-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  whileHover={{ y: -5 }}
                  onClick={() => navigate(`/public/events/${event.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="event-card-header">
                    <div className="event-icon">{getEventTypeIcon()}</div>
                    <div className="event-date">{formatDate(event.date)}</div>
                  </div>
                  <h3>{event.name}</h3>
                  <p>{event.description?.slice(0, 100) || 'Event details'}...</p>
                  <div className="event-meta">
                    <span>{event.club_name || 'Club Event'}</span>
                    {event.fee > 0 && <span>₹{event.fee}</span>}
                  </div>
                  <motion.button
                    className="event-register-btn"
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/public/events/${event.id}`);
                    }}
                  >
                    Learn More
                  </motion.button>
                </motion.div>
              );
            })
          )}
        </div>
        </div>

        {/* OPTIMIZED: Removed animated box-shadow, replaced with static drop-shadow filter and translateY hover */}
        <motion.div
          className="dashboard-cta-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={sharedTransition}
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="cta-content">
            <h2>Ready to Explore More?</h2>
            <p>Access your personalized dashboard to join clubs, register for events, and connect with the community</p>
            <motion.button
              className="prominent-dashboard-btn"
              onClick={handleEnterDashboard}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ y: 0, scale: 0.98 }}
            >
              {dashboardLabel}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );

  // Footer Section
  const Footer = () => (
    <motion.footer
      className="landing-footer"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-section">
            <h4>About NEXUS</h4>
            <p>The official platform for JSSATE college events and community engagement.</p>
          </div>

          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#hero">Home</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#events">Events</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h4>Contact</h4>
            <p>Email: events@jssate.edu</p>
            <p>Phone: +91 XXXX XXXXXX</p>
          </div>
        </div>

        <div className="footer-divider"></div>

        <div className="footer-bottom">
          <p className="copyright">
            &copy; 2025 JSSATE Events. All rights reserved.
          </p>
          <p className="credit">
            Designed and Developed by <strong>ISE (JSSATEB)</strong>
          </p>
          <p className="powered">
            Powered by <strong>Dean Academics, JSSATEB</strong>
          </p>
        </div>
      </div>
    </motion.footer>
  );

  return (
    <div 
      className="landing-page"
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Image - Same as dashboard */}
      <img
        src={bgImage}
        alt="background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      {/* Dark overlay for text readability */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(10, 0, 21, 0.4) 0%, rgba(26, 0, 51, 0.4) 50%, rgba(15, 0, 31, 0.4) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <Header />
        <HeroSection />
        <AboutSection />
        <EventsSection />
        <Footer />
      </div>
    </div>
  );
};

export default Landing;
